import React from 'react';
import type { VendorCombination } from '../vendorPresets';

interface MicrosoftPlanDetailModalProps {
  open: boolean;
  onClose: () => void;
  productName: string;
  combo: VendorCombination;
  price: number;
  planInclusions: string[] | null | undefined;
  pricingType?: 'perUser' | 'lumpSum';
}

export function MicrosoftPlanDetailModal(props: MicrosoftPlanDetailModalProps) {
  const { open, onClose, productName, combo, price, planInclusions, pricingType } = props;

  if (!open) return null;

  const billingLabel = combo.billingCycle === 'monthly' ? 'Monthly' : 'Annual';
  const commitmentLabel = combo.term === 0
    ? 'No commitment (month-to-month)'
    : `${combo.term}-month commitment`;
  const priceLabel = pricingType === 'perUser' ? '/user/month' : '/month';

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white shadow-2xl border border-gray-200">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-base font-semibold text-gray-900">{productName}</div>
              <div className="text-sm text-gray-500 mt-0.5">{combo.label}</div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Pricing summary */}
        <div className="px-5 py-4 bg-blue-50 border-b border-gray-200">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-gray-900">${price.toFixed(2)}</span>
            <span className="text-sm text-gray-600">{priceLabel}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {billingLabel} billing
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
              {commitmentLabel}
            </span>
          </div>
        </div>

        {/* Plan inclusions */}
        <div className="px-5 py-4 max-h-[40vh] overflow-auto">
          {(planInclusions?.length ?? 0) > 0 ? (
            <>
              <div className="text-sm font-medium text-gray-700 mb-3">Plan includes</div>
              <ul className="space-y-2">
                {planInclusions?.map((item, index) => (
                  <li key={index} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="text-sm text-gray-400">No plan inclusions defined yet</div>
              <div className="text-xs text-gray-400 mt-1">
                Add inclusions in the product editor to show them here
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default MicrosoftPlanDetailModal;
